/**
 * Executive Board Pack — daily brief, weekly summary, monthly board pack,
 * quarterly report and annual report. Every KPI comes from the Central
 * Metrics Service; AI commentary from GPT-5.2. Export to PDF / DOCX / PPTX.
 */
import React, { useEffect, useState } from "react";
import client from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader, KPITile, Section, Loading, StatusPill } from "../components/Common";
import { toast } from "sonner";
import {
  FileText, Download, Send, Loader2, Sparkles, Calendar, ClipboardCheck,
  Mail, ChevronRight,
} from "lucide-react";

const PERIODS = [
  { id: "daily_brief", label: "Daily Brief", days: 1 },
  { id: "weekly_summary", label: "Weekly Summary", days: 7 },
  { id: "monthly_pack", label: "Monthly Board Pack", days: 30 },
  { id: "quarterly", label: "Quarterly Report", days: 90 },
  { id: "annual", label: "Annual Report", days: 365 },
];

export default function BoardPack() {
  const { tenant } = useAuth();
  const [reports, setReports] = useState([]);
  const [selected, setSelected] = useState(null);
  const [generating, setGenerating] = useState("");
  const [sending, setSending] = useState(false);
  const [period, setPeriod] = useState("monthly_pack");

  const load = () => client.get("/board-pack/reports").then(({ data }) => setReports(data.items || []));
  useEffect(() => { load(); }, []);

  const openReport = (r) => client.get(`/board-pack/reports/${r.id}`).then(({ data }) => setSelected(data));

  const generate = async (p, sendEmail = false) => {
    setGenerating(p);
    try {
      const { data } = await client.post("/board-pack/generate", { period: p, send_email: sendEmail });
      toast.success(sendEmail ? "Generated & emailed to leadership" : "Report generated");
      await load();
      const { data: full } = await client.get(`/board-pack/reports/${data.report_id}`);
      setSelected(full);
    } catch { toast.error("Unable to generate report"); }
    finally { setGenerating(""); }
  };

  const emailSelected = async () => {
    if (!selected) return;
    setSending(true);
    try {
      await client.post("/board-pack/generate", { period: selected.period, send_email: true });
      toast.success("Emailed to leadership team");
      await load();
    } catch { toast.error("Delivery failed"); }
    finally { setSending(false); }
  };

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem("edu360.token");
  const downloadUrl = (fmt) => selected ? `${backendUrl}/api/education360/board-pack/reports/${selected.id}/export.${fmt}?token=${encodeURIComponent(token || "")}` : "#";

  return (
    <div data-testid="board-pack-page">
      <PageHeader
        kicker={`${tenant?.name} · Executive Board Pack Automation`}
        title="Reporting Engine"
        subtitle="Daily brief, weekly summary, monthly board pack, quarterly & annual reports — one click, three export formats."
      />

      <div className="hl-card p-5 mb-6" data-testid="generate-panel">
        <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">Generate a new report</div>
        <div className="grid md:grid-cols-5 gap-2">
          {PERIODS.map((p) => (
            <button
              key={p.id}
              onClick={() => { setPeriod(p.id); generate(p.id, false); }}
              disabled={!!generating}
              data-testid={`btn-gen-${p.id}`}
              className={`rounded border p-3 text-left transition hover:shadow-sm disabled:opacity-60 ${period === p.id ? "border-[color:var(--hl-orange)] bg-orange-50/40" : "border-slate-200 bg-white"}`}
            >
              <div className="flex items-center justify-between">
                <ClipboardCheck className="w-4 h-4 text-[color:var(--hl-orange)]" />
                {generating === p.id && <Loader2 className="w-3 h-3 animate-spin" />}
              </div>
              <div className="font-semibold mt-2 text-sm">{p.label}</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-0.5">{p.days} day window</div>
            </button>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2">
          <Section title="Report archive" subtitle="Every generated report — click to preview & export">
            <div className="hl-card overflow-hidden">
              <table className="hl-table w-full text-sm" data-testid="reports-table">
                <thead><tr><th>Period</th><th>Generated</th><th>Delivery</th></tr></thead>
                <tbody>
                  {reports.map((r) => (
                    <tr key={r.id} onClick={() => openReport(r)}
                        data-testid={`report-row-${r.id}`}
                        className={`cursor-pointer ${selected?.id === r.id ? "bg-orange-50/50" : ""}`}>
                      <td>
                        <div className="font-semibold text-xs">{r.period_label}</div>
                        <div className="text-[10px] uppercase tracking-widest text-slate-400">{r.triggered_by}</div>
                      </td>
                      <td className="text-xs mono">{new Date(r.generated_at).toLocaleString()}</td>
                      <td>{r.email_sent ? <StatusPill status="paid" /> : <StatusPill status="issued" />}</td>
                    </tr>
                  ))}
                  {!reports.length && <tr><td colSpan={3} className="text-center py-6 text-slate-400">Generate your first report above</td></tr>}
                </tbody>
              </table>
            </div>
          </Section>
        </div>

        <div className="lg:col-span-3">
          <Section title="Preview" subtitle={selected ? `${selected.period_label} · ${new Date(selected.generated_at).toLocaleString()}` : "Select a report"}>
            {!selected ? (
              <div className="hl-card p-10 text-center text-slate-400 text-sm" data-testid="preview-empty">
                <FileText className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                Select a report from the archive on the left to preview.
              </div>
            ) : (
              <div className="hl-card p-6" data-testid="preview-panel">
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.28em] text-[color:var(--hl-orange)] font-bold">Executive Report</div>
                    <div className="font-display font-bold text-xl mt-0.5">{selected.period_label}</div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <a href={downloadUrl("pdf")} target="_blank" rel="noopener noreferrer" data-testid="dl-pdf" className="hl-btn-outline text-xs px-3 py-2 inline-flex items-center gap-1.5"><Download className="w-3.5 h-3.5" /> PDF</a>
                    <a href={downloadUrl("docx")} target="_blank" rel="noopener noreferrer" data-testid="dl-docx" className="hl-btn-outline text-xs px-3 py-2 inline-flex items-center gap-1.5"><Download className="w-3.5 h-3.5" /> DOCX</a>
                    <a href={downloadUrl("pptx")} target="_blank" rel="noopener noreferrer" data-testid="dl-pptx" className="hl-btn-outline text-xs px-3 py-2 inline-flex items-center gap-1.5"><Download className="w-3.5 h-3.5" /> PPTX</a>
                    <button onClick={emailSelected} disabled={sending} data-testid="btn-email-report" className="hl-btn-accent text-xs px-3 py-2 inline-flex items-center gap-1.5">
                      {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Email to board
                    </button>
                  </div>
                </div>

                <div className="border-l-4 border-[color:var(--hl-orange)] bg-orange-50/40 p-4 rounded whitespace-pre-wrap text-sm leading-relaxed" data-testid="ai-commentary">
                  {selected.ai_commentary}
                </div>

                <div className="mt-5 grid md:grid-cols-2 gap-3" data-testid="report-sections">
                  {(selected.sections || []).map((s) => (
                    <div key={s.title} className="rounded border border-slate-200">
                      <div className="px-3 py-1.5 bg-slate-900 text-white text-[10px] uppercase tracking-widest font-bold">{s.title}</div>
                      <table className="w-full text-xs">
                        <tbody>
                          {s.rows.map(([k, v], i) => (
                            <tr key={i} className="border-b border-slate-100 last:border-b-0">
                              <td className="px-3 py-1.5 text-[11px] uppercase tracking-wider text-slate-500">{k}</td>
                              <td className="px-3 py-1.5 text-right font-semibold">{v}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Section>
        </div>
      </div>

      <div className="mt-6 text-[10px] uppercase tracking-widest text-slate-400 text-center">
        Scheduled: Weekly Monday 08:00 · Monthly 1st @ 08:00 Africa/Harare · All reports draw from the Central Metrics Service
      </div>
    </div>
  );
}
